/**
 * @author Angel Labrada
 * @since v0.0.1
 * @date 20/12/21
 */
import React, {memo, useCallback, useEffect, useMemo, useContext} from 'react';
import PropTypes from 'prop-types';
import {Form} from 'antd';
import ServicesApiService from '@/modules/settings/services/ServicesApiService';
import {useGetOneServices} from '@/modules/settings/hooks/useServices';
import {useClientFilter} from '@/hooks/useClientFilter';
import {FilterFactory, InFilter, TermFilter} from '@dofleini/query-builder';
import {useMe} from '@/modules/authentication/hooks/useUsers';
import {useGetWorkers} from '@/modules/auditorAudits/hooks/useGetWorkers';
import {useGetServicesTemplatesGoal} from '@/modules/audits/hooks/useGetTemplatesServicesGoal';
import AsyncSelect from '@/components/AsyncSelect';
import {AuditWizardContext} from '@/modules/auditorAudits/hooks/useWizard';

const serviceId = (value) => {
  return (typeof value === 'object') ? value?._id : value;
};

const AuditorAuditsSelectService = ({value, filters, onChange, isViewMode, ...props}) => {
  const {data} = useGetOneServices({id: serviceId(value)});
  const {currentEstablishmentEntity, setCurrentService, setValueGoal, setServicesTemplatesGoal} = useClientFilter();
  const {data: me} = useMe();
  const { data: worker } = useGetWorkers(new TermFilter({field: 'user', value: me?._id, objectId: true}).toQuery());
  const {data: servicesTemplatesGoal} = useGetServicesTemplatesGoal(currentEstablishmentEntity?.space);
  const wizardContext = useContext(AuditWizardContext);
  const setTemplateId = wizardContext?.setTemplateId;
  const setServiceId = wizardContext?.setServiceId;

  useEffect(() => {
    setServicesTemplatesGoal(servicesTemplatesGoal);
  }, [servicesTemplatesGoal, setServicesTemplatesGoal]);

  useEffect(() => {
    if (value) {
      const id = serviceId(value);
      setCurrentService(id);
      if (wizardContext && !wizardContext?.serviceId) {
        setServiceId && setServiceId(id);
      }
      const serviceGoal = servicesTemplatesGoal?.find((item) => {
        return item?.service === id;
      });
      setValueGoal(serviceGoal?.valueGoal);
    } else {
      setCurrentService(null);
      setValueGoal(0);
    }
  }, [value, servicesTemplatesGoal, setCurrentService, setValueGoal, setServiceId, wizardContext]);

  const handleOnChange = useCallback((value) => {
    setCurrentService(value);
    const serviceGoal = servicesTemplatesGoal?.find((item) => {
      return item?.service === value;
    });
    setValueGoal(serviceGoal?.valueGoal);
    setTemplateId && setTemplateId(null);
    onChange && onChange(value);
  }, [onChange, servicesTemplatesGoal, setCurrentService, setValueGoal, setTemplateId]);

  const filterServices = useMemo(() => {
    let associatedServices = [];
    if (worker?.length > 0) {
      associatedServices = worker[0]?.accessServices?.map((item) => {
        return item?.value;
      });
    }
    const services = servicesTemplatesGoal?.map((item) => {
      return item?.service;
    });
    if (services?.length > 0) {
      let associatedServicesEstablishments = [];
      if (associatedServices?.length > 0) {
        associatedServicesEstablishments = associatedServices?.filter(item => services?.includes(item));
      }
      return FilterFactory.add(filters, new InFilter({field: '_id', value: associatedServicesEstablishments?.length > 0 ? associatedServicesEstablishments : services})).toQuery();
    }
    return new InFilter({field: '_id', value: []}).toQuery();
  }, [filters, servicesTemplatesGoal, worker]);

  const hasServices = useMemo(() => {
    return servicesTemplatesGoal?.length > 0;
  }, [servicesTemplatesGoal]);

  if (isViewMode) {
    return (
      <>
        {data?.data?.name || 'N/A'}
      </>
    );
  }

  return (
    <AsyncSelect
      queryKey={'service'}
      filters={filterServices}
      service={ServicesApiService}
      value={value}
      deferFetchUntilOpen={true}
      onChange={handleOnChange}
      sort={{ code: 1 }}
      queryConfig={{ refetchOnWindowFocus: false, enabled: hasServices }}
      className="custom-select"
      {...props}
    />
  );
};

AuditorAuditsSelectService.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  filters: PropTypes.object,
  isViewMode: PropTypes.func,
};

export default memo(AuditorAuditsSelectService);

/**
 * cellRender
 * */
/**
 * This is not a component,
 * it is a function to render the Code component in a table cell
 * @param {String} text this is the value of the dataIndex of the table (we will ignored in this case)
 * @param {object} entity this is entity type
 * */
export const renderService = (text, entity) => {
  return (
    <AuditorAuditsSelectService value={text} entity={entity} isViewMode={true}/>
  );
};

export const renderSelectService = ({formItemProps, form, field, ...props}) => {
  const onChange = (value) => {
    form.setFieldsValue({
      [field.key]: value,
      template: null,
    });
  };

  const rules = [{
    required: field?.required,
    messaged: 'required',
  }];

  return (
    <Form.Item {...formItemProps} required={field?.required} rules={rules}>
      <AuditorAuditsSelectService onChange={onChange} {...props} placeholder={field?.placeholder} />
    </Form.Item>
  );
};

